// Phase A3's planted defects (gates A3.1 to A3.3), each beside what it breaks: the engine's own switches
// (@rpg/figure's MOTION_FAULTS and CHAIN_FAULTS) joined into the spike's one `__rpg.faults(changes)` record, plus the
// content defect the card schema test plants. `?faults=footProbeOff,...` switches them from the URL. The compile
// watchdog's defect is the harness's own `lateMaterial` (src/harness/faults.ts).
import type { TableFile } from "@rpg/content";
import { CHAIN_FAULTS, MOTION_FAULTS } from "@rpg/figure";
import type { FaultGroup } from "../faults";

/** The figure's fault names, as `?faults=` and `__rpg.faults()` take them. */
export const FIGURE_FAULT_NAMES = ["footProbeOff", "lookUnclamped", "springsUndamped"] as const;

/** The group for src/faults.ts's joined record. */
export const figureFaults: FaultGroup = {
  names: FIGURE_FAULT_NAMES,
  get: (n) => (n === "footProbeOff" ? MOTION_FAULTS.footProbeOff : n === "lookUnclamped" ? MOTION_FAULTS.lookUnclamped : n === "springsUndamped" ? CHAIN_FAULTS.undamped : false),
  set: (n, on) => {
    if (n === "footProbeOff") MOTION_FAULTS.footProbeOff = on;
    if (n === "lookUnclamped") MOTION_FAULTS.lookUnclamped = on;
    if (n === "springsUndamped") CHAIN_FAULTS.undamped = on;
  },
};

/** Turn on the figure defects `?faults=` names. */
export function applyFigureFaultSwitches(search = typeof location !== "undefined" ? location.search : ""): void {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  for (const n of FIGURE_FAULT_NAMES) if (on.includes(n)) figureFaults.set(n, true);
}

/** The card value the out-of-range defect breaks: the Wanderer's robe gets 99 segments (its range is 3 to 32). */
export const OUT_OF_RANGE = { table: "cards", id: "wanderer", piece: 1, field: "segments", value: 99 } as const;

/** Plant a card value outside its clamped range (gate A3.1's defect): the content check must reject the card. */
export function plantCardOutOfRange(tables: TableFile[]): void {
  const row = tables.find((t) => t.table === OUT_OF_RANGE.table)?.rows.find((r) => (r as { id?: string }).id === OUT_OF_RANGE.id) as { pieces?: Record<string, unknown>[] } | undefined;
  const piece = row?.pieces?.[OUT_OF_RANGE.piece];
  if (!piece || !(OUT_OF_RANGE.field in piece)) throw new Error("plantCardOutOfRange: the card value to break is missing (the defect did not apply)");
  piece[OUT_OF_RANGE.field] = OUT_OF_RANGE.value;
}
